import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { API, formatINR } from "@/constants";

const EASE = [0.16, 1, 0.3, 1];

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "fresh-cut", label: "Fresh Cut" },
  { id: "bouquets", label: "Bouquets" },
  { id: "artificial", label: "Artificial" },
  { id: "gifts", label: "Gifts" },
];

export default function ShopSection({ compact = false }) {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const { addItem, setIsOpen } = useCart();

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/products`, {
          params: category === "all" ? {} : { category },
        });
        setProducts(data);
      } catch {
        toast.error("Could not load the collection. Please refresh.");
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [category]);

  const handleAdd = (product) => {
    addItem(product);
    toast.success(`${product.name} added to cart`, {
      action: { label: "View Cart", onClick: () => setIsOpen(true) },
    });
  };

  return (
    <section id="collection" data-testid="shop-section" className={compact ? "py-24" : "py-28 lg:py-36"}>
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        {!compact && (
          <div className="mb-14">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: EASE }}
              className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-sage"
            >
              The Collection
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
              className="font-serif text-4xl font-medium tracking-tight text-forest md:text-6xl"
            >
              Cut this morning, <span className="italic text-marigold">yours by evening</span>
            </motion.h2>
          </div>
        )}

        <div className="mb-12 flex flex-wrap gap-3" data-testid="category-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              data-testid={`category-filter-${c.id}`}
              onClick={() => setCategory(c.id)}
              className={`rounded-full border px-6 py-2.5 text-xs font-bold uppercase tracking-[0.15em] transition-colors duration-300 ${
                category === c.id
                  ? "border-forest bg-forest text-cream"
                  : "border-forest/20 text-forest/60 hover:border-forest hover:text-forest"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="aspect-[3/4] animate-pulse bg-forest/5" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4" data-testid="products-grid">
            {products.map((p, i) => (
              <motion.article
                key={p.slug}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.8, delay: (i % 4) * 0.08, ease: EASE }}
                className="group"
                data-testid={`product-card-${p.slug}`}
              >
                <div
                  className={`relative overflow-hidden border border-forest/15 bg-forest/5 ${
                    i % 2 === 0 ? "rounded-t-[999px]" : "rounded-none"
                  } aspect-[3/4]`}
                >
                  <img
                    src={p.image}
                    alt={p.name}
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    loading="lazy"
                  />
                  {p.tag && (
                    <span className="absolute left-4 top-4 rounded-full bg-cream/90 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-forest backdrop-blur-sm">
                      {p.tag}
                    </span>
                  )}
                  <button
                    data-testid={`add-to-cart-${p.slug}`}
                    onClick={() => handleAdd(p)}
                    className="absolute bottom-4 right-4 flex h-12 w-12 items-center justify-center rounded-full bg-forest text-cream transition-all duration-300 hover:bg-marigold hover:text-forest md:translate-y-16 md:opacity-0 md:group-hover:translate-y-0 md:group-hover:opacity-100"
                    aria-label={`Add ${p.name} to cart`}
                  >
                    <Plus size={20} />
                  </button>
                </div>
                <div className="flex items-start justify-between gap-3 pt-5">
                  <div>
                    <h3 className="font-serif text-xl font-medium leading-snug text-forest">
                      {p.name}
                    </h3>
                    <p className="pt-1 text-xs leading-relaxed text-sage">{p.description}</p>
                  </div>
                  <span
                    className="whitespace-nowrap font-serif text-xl italic text-terracotta"
                    data-testid={`product-price-${p.slug}`}
                  >
                    {formatINR(p.price)}
                  </span>
                </div>
                <button
                  data-testid={`product-buy-${p.slug}`}
                  onClick={() => handleAdd(p)}
                  className="mt-3 w-full border border-forest/20 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-forest transition-colors duration-300 hover:bg-forest hover:text-cream md:hidden"
                >
                  Add to Cart
                </button>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
